import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ImpersonationStatusDto } from '@/api/generated';
import { useApiClient } from '@/api/useApiClient.util';

const IMPERSONATION_STATUS_KEY = ['impersonation', 'status'];

/**
 * Reads the current impersonation status (used by the global banner).
 * Returns impersonating=false for regular users; the endpoint requires only authentication.
 */
export const useImpersonationStatus = () => {
  const { adminImpersonationApi } = useApiClient();

  const { data, isLoading } = useQuery<ImpersonationStatusDto>({
    queryFn: async () => (await adminImpersonationApi.getImpersonationStatus()).data,
    queryKey: IMPERSONATION_STATUS_KEY,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
  });

  return { isLoadingStatus: isLoading, status: data };
};

/**
 * Starts impersonating the given user. On success the whole app is reloaded so that
 * /me (and React Query cache) are re-fetched as the impersonated user.
 */
export const useStartImpersonation = () => {
  const { adminImpersonationApi } = useApiClient();

  const { isPending, mutateAsync } = useMutation<ImpersonationStatusDto, AxiosError, string>({
    meta: { disableSuccessAlert: true },
    mutationFn: async (targetEmail: string) =>
      (await adminImpersonationApi.startImpersonation({ targetEmail })).data,
    onSuccess: () => {
      window.location.href = '/';
    },
  });

  return { isStarting: isPending, startImpersonation: mutateAsync };
};

/**
 * Stops impersonation (restores the real admin session) and reloads the app.
 */
export const useStopImpersonation = () => {
  const { adminImpersonationApi } = useApiClient();

  const { isPending, mutateAsync } = useMutation<ImpersonationStatusDto, AxiosError, void>({
    meta: { disableSuccessAlert: true },
    mutationFn: async () => (await adminImpersonationApi.stopImpersonation()).data,
    onSuccess: () => {
      window.location.href = '/';
    },
  });

  return { isStopping: isPending, stopImpersonation: mutateAsync };
};
